<script setup lang="ts">
// Boxed-chevron back affordance used at the top of multi-step flow cards
// (sign-up: Create Account / Details Fetched / OTP + its error states).
//
// Product-neutral chrome: it renders a Button and emits `back` — deciding what
// "back" means (step decrement, router.back(), a confirm modal) is the caller's
// job. Kept in uiKit rather than the auth module because nothing about it knows
// the sign-up flow.

import { computed } from 'vue'
import Button from '@uiKit/elements/Button.vue'

const props = withDefaults(
  defineProps<{
    /** Visible label. Rendered uppercase to match the flow-card spec. */
    label?: string
    disabled?: boolean
    /**
     * Uppercase the label (the flow-card default). Opt out where the board
     * writes the label as a destination name, e.g. "Log In".
     */
    shouldUppercase?: boolean
  }>(),
  { label: 'Back', disabled: false, shouldUppercase: true },
)

const emit = defineEmits<{ (e: 'back'): void }>()

// Uppercased here rather than via CSS `text-transform` so the accessible name
// and the rendered label stay identical.
const displayLabel = computed(() =>
  props.shouldUppercase ? props.label.toUpperCase() : props.label,
)
</script>

<template>
  <Button
    class="back-link"
    variant="text"
    color="secondary"
    size="small"
    icon="chevron-left"
    :label="displayLabel"
    :disabled="disabled"
    @click="emit('back')"
  />
</template>

<style scoped>
/* Strip the text-button's own padding so the 28px chevron box defines the
   left edge, then restore a 44px tall hit area for the persona's touch target. */
.back-link {
  padding: 0;
  gap: 0.75rem;
  min-height: 44px;
  /* Column flex parents (the sign-up Card) default to `align-items: stretch`,
     which would blow the button to full width and centre its label. Opt out
     here so the affordance stays top-left in any container. */
  align-self: flex-start;
  width: auto;
}

/* The chevron renders as the Button's icon span — box it to match the spec. */
.back-link :deep(.p-button-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-sm);
  background: var(--p-surface-0);
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
}

.back-link:not(:disabled):hover :deep(.p-button-icon) {
  border-color: var(--p-primary-600);
  background: var(--p-primary-50);
}
</style>
